import React, { type Dispatch, type SetStateAction } from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faEnvelopeOpen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useCreateNotificationSettings } from 'src/features/parkingManagement/api/NotificationsAPIs/useCreateNotificationSettings.ts';
import { useUpdateNotificationSettings } from 'src/features/parkingManagement/api/NotificationsAPIs/useUpdateNotificationSettings.ts';
import NotificationForm, {
    createNotificationSettingsFormSchema,
    type CreateNotificationSettingsFormSchemaValues,
} from 'src/features/parkingManagement/components/Notifications/NotificationForm.tsx';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import SquareIcon from 'src/registry/components/blocks/SquareIcon.tsx';
import { type DataTableNewEntryProps } from 'src/registry/components/table';
import { useToast } from 'src/registry/hooks/UseToast.ts';
import { Button } from 'src/registry/ui/Button.tsx';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogContent,
} from 'src/registry/ui/Dialog';
import { type GeneralSmartParkError } from 'src/types';
import { generalErrorMessages, generalMessages } from 'src/utils/messages.ts';

import type { Notification } from 'src/features/parkingManagement/types/notifications.ts';

interface NotificationModalProps extends DataTableNewEntryProps<Notification> {
    actionType: 'edit' | 'create';
    notification: Notification;
    refetch: () => void;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}

const NotificationModal = ({
    onSubmit: onSubmitCallback,
    actionType,
    notification,
    setIsModalOpen,
    refetch,
}: NotificationModalProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { toast } = useToast();

    const form = useForm<CreateNotificationSettingsFormSchemaValues>({
        resolver: zodResolver(createNotificationSettingsFormSchema),
        mode: 'onChange',
        defaultValues: {},
    });

    React.useEffect(() => {
        form.reset({
            notification_type: notification.notification_type,
            is_enabled: notification.is_enabled,
            recipient_roles: notification.recipient_roles,
        });
    }, [form, notification]);

    React.useEffect(() => {
        void form.trigger();
    }, [form.watch('is_enabled')]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        if (setIsModalOpen) {
            setIsModalOpen(isOpen);
        }
    };

    const onErrorHandle = (error: GeneralSmartParkError) => {
        const errorData = error.response?.data;
        const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
        const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

        toast({
            title: errorCode,
            description: errorMessage,
            variant: 'destructive',
        });
    };

    const { mutate: createNotificationSetting } = useCreateNotificationSettings({
        onSuccess: () => {
            void refetch();
            onOpenChange(false);
            toast({
                title: formatMessage(parkingManagementMessages.notificationSettings),
                description: formatMessage(parkingManagementMessages.notificationSetSuccessfully),
            });
            if (onSubmitCallback) {
                onSubmitCallback();
            }
        },
        onError: (error) => onErrorHandle(error),
    });

    const { mutate: updateNotificationSetting, isPending: isPendingUpdate } = useUpdateNotificationSettings({
        onSuccess: () => {
            void refetch();
            onOpenChange(false);
            toast({
                title: formatMessage(parkingManagementMessages.notificationSettings),
                description: formatMessage(parkingManagementMessages.notificationSetSuccessfully),
            });
            if (onSubmitCallback) {
                onSubmitCallback();
            }
        },
        onError: (error) => onErrorHandle(error),
    });

    const onSubmit = (values: CreateNotificationSettingsFormSchemaValues) => {
        if (!parkingId) return;
        const { is_enabled, recipient_roles, notification_type } = values;
        const rolesToSendToAPI = is_enabled ? recipient_roles : [];

        const submitData = {
            parkingId: parkingId,
            recipient_roles: rolesToSendToAPI,
            is_enabled,
        };

        const createSubmitData = {
            notification_type: notification_type,
            ...submitData,
        };

        if (actionType === 'create') {
            createNotificationSetting(createSubmitData);
        } else {
            updateNotificationSetting({ id: notification.id, ...submitData });
        }
        form.reset(values);
    };

    return (
        <Dialog open={true} onOpenChange={setIsModalOpen}>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faEnvelopeOpen} />
                        <div>
                            <DialogTitle>{`${formatMessage(generalMessages.edit)} ${formatMessage(parkingManagementMessages.notification).toLowerCase()}`}</DialogTitle>
                            <DialogDescription>
                                <FormattedMessage {...parkingManagementMessages.notificationModalSubtitle} />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <NotificationForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        disabled={!form.formState.isDirty || !form.formState.isValid}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPendingUpdate ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        <FormattedMessage {...parkingManagementMessages.saveNotificationSettings} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default NotificationModal;
